import { useEffect, useRef, useState } from 'react'
import { useCV } from '../useCV'
import { useCVSource } from '../useCVSource'
import { getKernel, convolveGray, convolveAt } from '../convolution'
import { Panel, Caption, sceneShell } from './ui'

const W = 96, H = 72

// 2D rebuild of the convolution scene. The heavy lifting (grayscale source,
// kernel maths) is the same code the 3D version used — we just paint to two
// visible <canvas> elements instead of uploading THREE textures.
export function Conv2D() {
  const kernelId = useCV(s => s.kernelId)
  const useWebcam = useCV(s => s.useWebcam)
  const source = useCVSource({ W, H, useWebcam, fallbackSrc: '/cv/ref-cat.png' })

  const inputRef = useRef(null)
  const outputRef = useRef(null)
  const sample = useRef({ x: W >> 1, y: H >> 1 })
  const frame = useRef(0)
  const outGray = useRef(new Uint8ClampedArray(W * H))
  const [math, setMath] = useState(null)

  const kernel = getKernel(kernelId)
  const kernelRef = useRef(kernel)
  kernelRef.current = kernel

  useEffect(() => {
    const inCtx = inputRef.current.getContext('2d')
    const outCtx = outputRef.current.getContext('2d')
    const inImg = inCtx.createImageData(W, H)
    const outImg = outCtx.createImageData(W, H)
    let raf

    const loop = () => {
      source.update()
      if (source.ready) {
        const gray = source.gray
        const k = kernelRef.current
        convolveGray(gray, W, H, k, outGray.current)

        frame.current++
        if (frame.current === 1 || frame.current % 10 === 0) {
          const s = sample.current
          s.x += 7
          if (s.x >= W - 1) { s.x = 1; s.y = ((s.y + 9) % (H - 2)) + 1 }
          if (s.y < 1) s.y = 1
          setMath({ x: s.x, y: s.y, ...convolveAt(gray, W, s.x, s.y, k) })
        }

        const id = inImg.data
        for (let i = 0; i < W * H; i++) {
          const v = gray[i]
          id[i * 4] = id[i * 4 + 1] = id[i * 4 + 2] = v; id[i * 4 + 3] = 255
        }
        paintMarker(id, sample.current.x, sample.current.y)
        inCtx.putImageData(inImg, 0, 0)

        const od = outImg.data
        for (let i = 0; i < W * H; i++) {
          const v = outGray.current[i]
          od[i * 4] = od[i * 4 + 1] = od[i * 4 + 2] = v; od[i * 4 + 3] = 255
        }
        outCtx.putImageData(outImg, 0, 0)
      }
      raf = requestAnimationFrame(loop)
    }
    loop()
    return () => cancelAnimationFrame(raf)
  }, [source])

  const live = source.mode === 'webcam'
  const [mobile, setMobile] = useState(() => window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  const cvSize = mobile ? { width: 220, height: 165 } : { width: 260, height: 195 }
  const cellSize = mobile ? 44 : 54
  const cellFont = mobile ? 10 : 17
  const labelFont = mobile ? 9 : 12
  const subFont = mobile ? 9.5 : 12.5

  return (
    <div style={{ ...sceneShell, padding: mobile ? '80px 20px 90px' : '90px 24px 160px' }}>
      <div style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', alignItems: 'center', justifyContent: 'center', gap: mobile ? 14 : 28, flexWrap: 'wrap', width: mobile ? '100%' : undefined }}>
        {/* INPUT */}
        <Stage label="INPUT" sub={live ? '● live · grayscale' : 'grayscale · 96×72'} subColor={live ? '#22c55e' : undefined} labelFont={labelFont} subFont={subFont}>
          <canvas ref={inputRef} width={W} height={H} style={{ ...canvasStyle, ...cvSize }} />
        </Stage>

        <Arrow mobile={mobile} />

        {/* KERNEL */}
        <Stage label="KERNEL · 3×3" sub={kernel.desc} labelFont={labelFont} subFont={subFont}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: mobile ? 3 : 4, padding: mobile ? 4 : 6 }}>
            {kernel.k.flatMap((row, r) => row.map((val, c) => {
              const color = val > 0 ? '#22c55e' : val < 0 ? '#ef4444' : 'rgba(255,255,255,0.5)'
              return (
                <div key={`${r}-${c}`} style={{
                  width: cellSize, height: cellSize, display: 'grid', placeItems: 'center',
                  borderRadius: 8, fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: cellFont, fontWeight: 700,
                  color, background: `${color}1a`, border: `1px solid ${color}55`,
                }}>
                  {kernel.div !== 1 ? `${val}/${kernel.div}` : val}
                </div>
              )
            }))}
          </div>
          <div style={{ fontSize: 12.5, color: '#a855f7', fontFamily: "'JetBrains Mono', ui-monospace, monospace", marginTop: 4 }}>{kernel.label}</div>
        </Stage>

        <Arrow mobile={mobile} />

        {/* FEATURE MAP */}
        <Stage label="FEATURE MAP" sub="one filter → one feature map" labelFont={labelFont} subFont={subFont}>
          <canvas ref={outputRef} width={W} height={H} style={{ ...canvasStyle, ...cvSize }} />
        </Stage>
      </div>

      {/* THE MATH */}
      <MathRow math={math} mobile={mobile} />

      <Caption>
        A kernel slides over every pixel (stride 1), multiplies its 9 neighbours by the weights and sums them.
        Real CNNs stack many such filters across <em>all</em> input channels — and <strong>learn</strong> the weights
        from data instead of hand-picking them.
      </Caption>
    </div>
  )
}

function Stage({ label, sub, subColor, children, labelFont = 12, subFont = 12.5 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <div style={{ fontSize: labelFont, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.55)', fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{label}</div>
      <Panel style={{ padding: 0, overflow: 'hidden', display: 'grid', placeItems: 'center' }}>{children}</Panel>
      <div style={{ fontSize: subFont, color: subColor || 'rgba(255,255,255,0.4)', fontFamily: "'JetBrains Mono', ui-monospace, monospace", textAlign: 'center', maxWidth: 220 }}>{sub}</div>
    </div>
  )
}

function MathRow({ math, mobile }) {
  if (!math) return null
  const { cells, sum, out } = math
  return (
    <Panel style={{
      display: 'flex', flexDirection: mobile ? 'column' : 'row', alignItems: 'center', justifyContent: 'center', gap: mobile ? 10 : 18, flexWrap: 'wrap',
      padding: mobile ? '14px 16px' : '12px 22px', marginTop: mobile ? 14 : 26, fontFamily: "'JetBrains Mono', ui-monospace, monospace",
      width: mobile ? '100%' : undefined,
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 3 }}>
          {cells.map((c, i) => (
            <div key={i} style={{ width: 30, fontSize: 13.5, color: '#e0e7ff', textAlign: 'center' }}>{Math.round(c.p)}</div>
          ))}
        </div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>pixel patch</div>
      </div>
      <div style={{ fontSize: 22, color: '#a855f7' }}>⊙</div>
      <div style={{ fontSize: 14.5, color: '#93c5fd' }}>weights</div>
      <div style={{ fontSize: 20, color: 'rgba(255,255,255,0.6)' }}>=</div>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 15, color: '#fff' }}>Σ = {Math.round(sum)}</div>
        <div style={{ fontSize: 15, color: '#22c55e', fontWeight: 700 }}>out = {out}</div>
      </div>
    </Panel>
  )
}

function Arrow({ mobile }) {
  return <div style={{ color: '#6366f1', fontSize: 26, opacity: 0.7, transform: mobile ? 'rotate(90deg)' : 'none' }}>→</div>
}

const canvasStyle = {
  width: 260, height: 195, imageRendering: 'pixelated', display: 'block',
}

function paintMarker(data, sx, sy) {
  const set = (x, y) => {
    if (x < 0 || y < 0 || x >= W || y >= H) return
    const i = (y * W + x) * 4
    data[i] = 168; data[i + 1] = 85; data[i + 2] = 247; data[i + 3] = 255
  }
  for (let d = -1; d <= 1; d++) {
    set(sx + d, sy - 2); set(sx + d, sy + 2)
    set(sx - 2, sy + d); set(sx + 2, sy + d)
  }
}
